/**
 * Story 059 — TanStack Query hooks for the turbine-models feature.
 *
 * Mirrors `apps/web/src/features/sites/queries/hooks.ts` shape. Lifts
 * the `assetsFetch` helper directly from Sites (same `/api/assets/v1`
 * namespace, no duplication). Per `tanstack-query-conventions` skill:
 * hierarchical keys + per-query `staleTime` + zero retries on
 * mutations.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { assetsFetch } from "../../sites/queries/assetsFetch.js";
import { assetModelsKeys } from "./keys.js";
import type {
  AssetModelDetail,
  AssetModelSpecificationParameterList,
  CreateAssetModelBody,
  ListAssetModelsExpandedParams,
  ListAssetModelsExpandedResponse,
  UpdateAssetModelBody,
} from "./types.js";

function listUrl(params: ListAssetModelsExpandedParams): string {
  const sp = new URLSearchParams();
  // `expanded=true` is the discriminator that routes the request to
  // the expanded list-handler (spec 063) vs spec 062's lean
  // cascading-dropdown projection.
  sp.set("expanded", "true");
  sp.set("page", String(params.page ?? 1));
  sp.set("pageSize", String(params.pageSize ?? 25));
  if (params.search && params.search.length > 0) {
    sp.set("search", params.search);
  }
  if (params.sort) sp.set("sort", params.sort);
  if (params.includeInactive === true) sp.set("includeInactive", "true");
  // Manufacturer CSV — alphabetical join for determinism (cache-key
  // canonicalisation).
  if (params.manufacturer && params.manufacturer.length > 0) {
    const sorted = [...params.manufacturer].sort((a, b) => a.localeCompare(b));
    sp.set("manufacturer", sorted.join(","));
  }
  // assetTypeId CSV — alphabetical UUID join. AC6 expects the regex
  // `/assetTypeId=<wind-turbine-uuid>(%2C|,)<powerline-uuid>/` after
  // picking two Types so the canonicalisation matters. UUIDs sort
  // lexicographically which gives a deterministic order across
  // sessions.
  if (params.assetTypeId && params.assetTypeId.length > 0) {
    const sorted = [...params.assetTypeId].sort((a, b) => a.localeCompare(b));
    sp.set("assetTypeId", sorted.join(","));
  }
  if (params.scope !== undefined) sp.set("scope", params.scope);
  return `/asset-models?${sp.toString()}`;
}

export function useAssetModelsListQuery(params: ListAssetModelsExpandedParams) {
  return useQuery<ListAssetModelsExpandedResponse>({
    queryKey: assetModelsKeys.list(params),
    queryFn: () =>
      assetsFetch<ListAssetModelsExpandedResponse>(listUrl(params)),
    staleTime: 30_000,
    retry: false,
  });
}

export function useAssetModelDetailQuery(id: string | null | undefined) {
  return useQuery<AssetModelDetail>({
    queryKey: assetModelsKeys.detail(id ?? "__none__"),
    queryFn: () => assetsFetch<AssetModelDetail>(`/asset-models/${id ?? ""}`),
    enabled: typeof id === "string" && id.length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * ASM-SPEC-01 — the Specifications editor's suggestion list.
 *
 * `{ items }` = the platform catalogue (`scope: global`, in the BE's
 * `sort_order`) followed by the pairs THIS tenant has saved that are not
 * canonical (`scope: tenant`, most-used first). The BE learns a tenant pair
 * on every successful Save, which is why both mutations below invalidate
 * this key.
 *
 * `retry: false` and no error UI on purpose: the catalogue is a suggestion.
 * If this read fails, the editor degrades to plain free text (AC-29) — it
 * must never be the reason an operator cannot record a specification.
 */
export function useSpecificationParametersQuery() {
  return useQuery<AssetModelSpecificationParameterList>({
    queryKey: assetModelsKeys.specificationParameters(),
    queryFn: () =>
      assetsFetch<AssetModelSpecificationParameterList>(
        "/asset-model-specification-parameters",
      ),
    staleTime: 60_000,
    retry: false,
  });
}

export function useCreateAssetModelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAssetModelBody) =>
      assetsFetch<AssetModelDetail>("/asset-models", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetModelsKeys.lists() });
      // ASM-SPEC-01 AC-32 — the Save may have taught the tenant a new
      // (parameter, unit) pair; the next row the operator adds should offer
      // it without a reload.
      void qc.invalidateQueries({
        queryKey: assetModelsKeys.specificationParameters(),
      });
    },
  });
}

/**
 * SB2-W-05 (assets/v1@4.5.0) — global-scope create. Identical wire
 * shape to `useCreateAssetModelMutation` but sets `tenantId: null` so
 * the BE writes to `public.global_asset_models` (platform_admin only).
 */
export function useCreateGlobalAssetModelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAssetModelBody) =>
      assetsFetch<AssetModelDetail>("/asset-models", {
        method: "POST",
        body: { ...body, tenantId: null },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetModelsKeys.lists() });
      // A platform_admin saving a GLOBAL model still teaches the tenant they
      // are acting in (D-3 / AC-17), so this path invalidates too.
      void qc.invalidateQueries({
        queryKey: assetModelsKeys.specificationParameters(),
      });
    },
  });
}

export interface UpdateAssetModelArgs {
  id: string;
  body: UpdateAssetModelBody;
}

export function useUpdateAssetModelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: UpdateAssetModelArgs) =>
      assetsFetch<AssetModelDetail>(`/asset-models/${id}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: assetModelsKeys.lists() });
      void qc.invalidateQueries({
        queryKey: assetModelsKeys.detail(updated.id),
      });
      void qc.invalidateQueries({
        queryKey: assetModelsKeys.specificationParameters(),
      });
    },
  });
}

export function useDeactivateAssetModelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      assetsFetch<AssetModelDetail>(`/asset-models/${id}`, {
        method: "DELETE",
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: assetModelsKeys.lists() });
      void qc.invalidateQueries({
        queryKey: assetModelsKeys.detail(updated.id),
      });
    },
  });
}

export function useReactivateAssetModelMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      assetsFetch<AssetModelDetail>(`/asset-models/${id}/reactivate`, {
        method: "POST",
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: assetModelsKeys.lists() });
      void qc.invalidateQueries({
        queryKey: assetModelsKeys.detail(updated.id),
      });
    },
  });
}
